import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import LoadingSpinner from '../LoadingSpinner';

const emptyLine = {
  original_sku: '',
  item_name: '',
  qty_expected: 1,
  expected_package_type: 'Each',
  vendor: '',
  original_description: '',
  gross_weight_lbs: '',
  declared_unit_value: '',
  project_name: '',
  project_code: '',
  notes: ''
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const normalizeLine = (line = {}) => ({
  id: line.id || null,
  original_sku: line.original_sku || '',
  item_name: line.item_name || line.MatchedInventoryItem?.product_name || line.original_sku || '',
  qty_expected: line.qty_expected || 1,
  expected_package_type: line.expected_package_type || 'Each',
  vendor: line.vendor || line.MatchedInventoryItem?.vendor || '',
  original_description: line.original_description || line.MatchedInventoryItem?.description || '',
  gross_weight_lbs: line.gross_weight_lbs || '',
  declared_unit_value: line.declared_unit_value || '',
  project_name: line.project_name || '',
  project_code: line.project_code || '',
  notes: line.notes || '',
  can_edit: line.can_edit !== false,
  locked_reason: line.locked_reason || null,
  _delete: false
});

const ManifestEditForm = ({
  requestId,
  api,
  onCancel,
  onSaved
}) => {
  const [manifest, setManifest] = useState(null);
  const [lines, setLines] = useState([]);
  const [error, setError] = useState(null);

  const { data, isLoading, error: loadError } = useQuery(
    ['manifest-edit', requestId],
    () => api.getManifestEdit(requestId),
    {
      enabled: !!requestId
    }
  );

  useEffect(() => {
    if (!data?.manifest) return;

    const loadedManifest = data.manifest;
    setManifest({
      name: loadedManifest.name || '',
      expected_arrival_date: toDateInputValue(loadedManifest.expected_arrival_date),
      notes: loadedManifest.notes || '',
      destination_warehouse_id: loadedManifest.destination_warehouse_id || '',
      receivingInstructions: loadedManifest.receiving_instructions || {}
    });
    setLines((loadedManifest.InboundProjectLines || []).map(normalizeLine));
  }, [data]);

  const mutation = useMutation(
    (payload) => api.updateManifestEdit(requestId, payload),
    {
      onSuccess: () => {
        setError(null);
        if (onSaved) onSaved();
      },
      onError: (err) => {
        setError(err?.response?.data?.message || err.message || 'Failed to save manifest');
      }
    }
  );

  const updateManifest = (field, value) => {
    setManifest((current) => ({ ...current, [field]: value }));
  };

  const updateLine = (index, field, value) => {
    setLines((current) => {
      const next = [...current];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addLine = () => {
    setLines((current) => [...current, { ...emptyLine, can_edit: true, _delete: false }]);
  };

  const removeLine = (index) => {
    setLines((current) => {
      const line = current[index];
      if (!line.id) return current.filter((_, lineIndex) => lineIndex !== index);
      const next = [...current];
      next[index] = { ...line, _delete: true };
      return next;
    });
  };

  const restoreLine = (index) => {
    setLines((current) => {
      const next = [...current];
      next[index] = { ...next[index], _delete: false };
      return next;
    });
  };

  const validate = () => {
    if (!manifest?.name?.trim()) return 'Manifest name is required';
    const activeLines = lines.filter((line) => !line._delete);
    if (activeLines.length === 0) return 'At least one item is required';
    const invalidLine = activeLines.find((line) => !line.original_sku?.trim() || Number(line.qty_expected) < 1);
    if (invalidLine) return 'Each item needs a SKU and quantity of at least 1';
    return null;
  };

  const handleSave = () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    mutation.mutate({
      name: manifest.name,
      expected_arrival_date: manifest.expected_arrival_date || null,
      notes: manifest.notes,
      destination_warehouse_id: manifest.destination_warehouse_id || undefined,
      receivingInstructions: manifest.receivingInstructions,
      lines
    });
  };

  if (loadError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Failed to load manifest details. Please go back and try again.
      </div>
    );
  }

  if (isLoading || !manifest) {
    return <LoadingSpinner size="md" label="Loading manifest..." />;
  }

  const visibleLines = lines.filter((line) => !line._delete);
  const removedLines = lines.filter((line) => line._delete);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Manifest Name</label>
          <input
            type="text"
            value={manifest.name}
            onChange={(event) => updateManifest('name', event.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Expected Arrival Date</label>
          <input
            type="date"
            value={manifest.expected_arrival_date}
            onChange={(event) => updateManifest('expected_arrival_date', event.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Notes</label>
        <textarea
          value={manifest.notes}
          onChange={(event) => updateManifest('notes', event.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
      </div>

      <div className="border-t border-gray-200 pt-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Items</h3>
            <p className="mt-1 text-sm text-gray-500">
              Items with received or held quantity are locked to prevent inventory count issues.
            </p>
          </div>
          <button
            type="button"
            onClick={addLine}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
          >
            Add Item
          </button>
        </div>

        <div className="mt-4 space-y-4">
          {visibleLines.map((line, index) => {
            const actualIndex = lines.indexOf(line);
            const locked = line.can_edit === false;
            return (
              <div key={line.id || `new-${actualIndex}`} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Line Item #{index + 1}</p>
                    {locked && (
                      <p className="mt-1 text-xs text-amber-700">{line.locked_reason}</p>
                    )}
                  </div>
                  {!locked && (
                    <button
                      type="button"
                      onClick={() => removeLine(actualIndex)}
                      className="text-sm font-medium text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">SKU</label>
                    <input
                      type="text"
                      value={line.original_sku}
                      disabled={locked}
                      onChange={(event) => updateLine(actualIndex, 'original_sku', event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={line.qty_expected}
                      disabled={locked}
                      onChange={(event) => updateLine(actualIndex, 'qty_expected', event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Item Name</label>
                    <input
                      type="text"
                      value={line.item_name}
                      disabled={locked}
                      onChange={(event) => updateLine(actualIndex, 'item_name', event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Package Type</label>
                    <select
                      value={line.expected_package_type}
                      disabled={locked}
                      onChange={(event) => updateLine(actualIndex, 'expected_package_type', event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm"
                    >
                      <option value="Each">Each</option>
                      <option value="Carton">Carton</option>
                      <option value="Pallet">Pallet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Vendor</label>
                    <input
                      type="text"
                      value={line.vendor}
                      disabled={locked}
                      onChange={(event) => updateLine(actualIndex, 'vendor', event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Approx. Value ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.declared_unit_value}
                      disabled={locked}
                      onChange={(event) => updateLine(actualIndex, 'declared_unit_value', event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Project Name</label>
                    <input
                      type="text"
                      value={line.project_name}
                      disabled={locked}
                      onChange={(event) => updateLine(actualIndex, 'project_name', event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Project Code</label>
                    <input
                      type="text"
                      value={line.project_code}
                      disabled={locked}
                      onChange={(event) => updateLine(actualIndex, 'project_code', event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm disabled:bg-gray-100 disabled:text-gray-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Notes</label>
                  <input
                    type="text"
                    value={line.notes}
                    onChange={(event) => updateLine(actualIndex, 'notes', event.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {removedLines.length > 0 && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm font-medium text-red-800">Removed items pending save</p>
            {removedLines.map((line) => {
              const actualIndex = lines.indexOf(line);
              return (
                <div key={line.id || actualIndex} className="mt-2 flex items-center justify-between text-sm text-red-700">
                  <span>{line.original_sku} - {line.item_name}</span>
                  <button type="button" onClick={() => restoreLine(actualIndex)} className="font-medium">
                    Undo
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={mutation.isLoading}
          className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          {mutation.isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default ManifestEditForm;
